import React from "react";
import { Section, SerifH, Eyebrow, PrimaryButton, Card } from "../components/ui-kit/Primitives";
import { Heart, ShieldCheck, Sprout, HandHeart, Users, Compass, Award, Building, Sparkles } from "lucide-react";

const values = [
  { icon: ShieldCheck, t: "Integrity", d: "We work with honesty, transparency, and accountability." },
  { icon: Users, t: "Community", d: "We build with and for the people we serve." },
  { icon: Heart, t: "Healing", d: "Care is the center of every system we design." },
  { icon: Compass, t: "Accountability", d: "We show up, follow through, and measure what matters." },
  { icon: Building, t: "Structure", d: "Strong frameworks make compassionate work possible." },
  { icon: Sparkles, t: "Empowerment", d: "We hand people the tools to lead themselves forward." },
  { icon: HandHeart, t: "Cultural Awareness", d: "Lived experience and cultural respect shape our practice." },
  { icon: Sprout, t: "Long-term Sustainability", d: "We build for durability, not just launch day." },
  { icon: Award, t: "Service with Purpose", d: "Everything we do leads back to people and outcomes." },
];

const About = () => (
  <div data-testid="about-page">
    <Section className="pt-16">
      <div className="max-w-3xl">
        <Eyebrow>About KBX</Eyebrow>
        <SerifH as="h1" className="text-5xl sm:text-6xl">
          Bridging wellness, lived experience, and business structure.
        </SerifH>
        <p className="mt-7 text-lg text-slate-600 font-light leading-relaxed">
          KBX Pathways To Wellness was created to bridge the gap between wellness, lived
          experience, business structure, and community transformation.
        </p>
      </div>
    </Section>

    <Section className="bg-brand-mist/60 pt-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <SerifH className="text-3xl sm:text-4xl">Peer support is a calling — and it needs a foundation.</SerifH>
        </div>
        <div className="lg:col-span-6 space-y-6 text-slate-700 leading-relaxed font-light">
          <p>
            We believe peer support is more than a service. It is a calling rooted in lived
            experience, empathy, accountability, and practical guidance.
          </p>
          <p>
            At the same time, we understand that peer support agencies need more than passion to
            succeed. They need structure, compliance, systems, leadership, documentation, funding
            readiness, and a clear service model.
          </p>
          <p>
            KBX exists to help agencies build correctly from the beginning — while also preparing
            to serve individuals and families through compassionate peer support services.
          </p>
        </div>
      </div>
    </Section>

    <Section>
      <Eyebrow>Our Values</Eyebrow>
      <SerifH className="text-4xl sm:text-5xl mb-12">The principles that guide our work.</SerifH>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {values.map(({ icon: Icon, t, d }) => (
          <Card key={t} className="p-7">
            <div className="w-11 h-11 rounded-xl bg-brand-green/10 flex items-center justify-center text-brand-green mb-4">
              <Icon className="w-5 h-5" />
            </div>
            <h3 className="font-serif text-2xl text-brand-green">{t}</h3>
            <p className="text-slate-600 mt-2 leading-relaxed text-[15px]">{d}</p>
          </Card>
        ))}
      </div>
    </Section>

    <Section className="bg-brand-navy text-white">
      <Eyebrow color="gold">Our Approach</Eyebrow>
      <SerifH className="text-4xl sm:text-5xl text-white max-w-3xl">
        Wellness-centered care meets operational structure.
      </SerifH>
      <p className="mt-6 text-slate-300 text-lg font-light max-w-3xl leading-relaxed">
        Our approach combines wellness-centered care, operational structure, business consulting,
        and community-based support. We help organizations move from idea to implementation with
        clarity, strategy, and accountability.
      </p>
      <div className="mt-10">
        <PrimaryButton to="/contact" testid="about-cta-book" className="bg-brand-gold text-brand-navy hover:bg-white">
          Schedule a Consultation
        </PrimaryButton>
      </div>
    </Section>
  </div>
);

export default About;
